import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'

import { Breadcrumb } from '@/components/Breadcrumb'
import { Icon } from '@/components/Icon'
import { ViewTabs, type ViewTab } from '@/components/ViewTabs'
import { ActivityFeed } from '@/features/activity/ActivityFeed'
import { BoardPage } from '@/features/boards/BoardPage'
import { DocumentLibrary } from '@/features/documents/DocumentLibrary'
import { DocumentPanel } from '@/features/documents/DocumentPanel'
import type { ProjectDocument } from '@/features/documents/api'
import { ProjectSettings } from '@/features/projects/ProjectSettings'
import { Timeline } from '@/features/projects/Timeline'
import { WorkflowPanel } from '@/features/projects/WorkflowPanel'
import { fetchWorkItems } from '@/features/work-items/api'
import { TaskCalendar } from '@/features/work-items/TaskCalendar'
import { TaskList } from '@/features/work-items/TaskList'
import { TaskTable } from '@/features/work-items/TaskTable'
import { TaskPanel } from '@/features/work-items/TaskPanel'
import { fetchTimeline } from '@/features/workflow/api'
import { ViewBar } from '@/features/views/ViewBar'
import type { SavedView, ViewLayout } from '@/features/views/api'
import { api, type ApiError } from '@/lib/api/client'
import { useLiveProject } from '@/lib/realtime/useLiveProject'
import type { Project } from '@/types/domain'
import type { WorkItem } from '@/types/work'
import type { Timeline as TimelineData } from '@/types/workflow'

async function fetchProject(key: string): Promise<Project> {
  const { data } = await api.get<{ data: Project }>(`/projects/${key}`)

  return data.data
}

/** The five the server accepts as a saved view's rendering (View::LAYOUTS). */
const SAVEABLE = ['list', 'table', 'board', 'calendar', 'timeline'] as const

/** Documents, activity and overview are screens of the project, not renderings of its work. */
type Layout = ViewLayout | 'documents' | 'activity' | 'overview'

/**
 * One project, several views of it.
 *
 * List, board and timeline are three renderings of the same query rather than
 * three destinations (ADR-0003), which is why they are tabs here and not
 * separate places in the navigation. Opening a card lifts a panel over whichever
 * view you were in, so reading one does not cost your place among the rest.
 */
export function ProjectPage() {
  const { t } = useTranslation()
  const { key } = useParams()
  const [layout, setLayout] = useState<Layout>('list')
  const [open, setOpen] = useState<WorkItem | null>(null)
  const [openDocument, setOpenDocument] = useState<ProjectDocument | null>(null)
  const [editing, setEditing] = useState(false)
  const [view, setView] = useState<SavedView | null>(null)

  const project = useQuery<Project, ApiError>({
    queryKey: ['project', key],
    queryFn: () => fetchProject(key as string),
    enabled: key !== undefined,
    retry: false,
  })

  const items = useQuery<WorkItem[], ApiError>({
    queryKey: ['work-items', { project: key, view: view?.id ?? null }],
    queryFn: () =>
      fetchWorkItems(
        view === null
          ? { project: key as string }
          : { project: key as string, view: view.id },
      ),
    enabled: key !== undefined && ['list', 'table', 'calendar'].includes(layout),
    retry: false,
  })

  const timeline = useQuery<TimelineData, ApiError>({
    queryKey: ['timeline', key],
    queryFn: () => fetchTimeline(key as string),
    enabled: key !== undefined && layout === 'timeline',
    retry: false,
  })

  // Somebody else moving a card on this project reaches this screen too.
  useLiveProject(key ?? null)

  if (project.isPending) {
    return <p className="text-ink-500 font-mono text-xs">{t('common.loading')}</p>
  }

  // Out of reach is not found, and the message says nothing more than that.
  if (project.isError) {
    return <h1 className="text-ink-900 text-xl font-semibold">{t('project.notFound')}</h1>
  }

  const tabs: ViewTab[] = [
    { id: 'list', label: t('view.list'), icon: 'list' },
    { id: 'table', label: t('view.table'), icon: 'chart' },
    { id: 'board', label: t('view.board'), icon: 'board' },
    { id: 'calendar', label: t('view.calendar'), icon: 'calendar' },
    { id: 'timeline', label: t('view.timeline'), icon: 'timeline' },
    { id: 'documents', label: t('document.title'), icon: 'file' },
    { id: 'activity', label: t('activity.title'), icon: 'clock' },
    { id: 'overview', label: t('view.overview'), icon: 'folder' },
  ]

  return (
    <div>
      <Breadcrumb
        trail={[
          { label: t('nav.portfolio'), to: '/portfolio' },
          { label: project.data.division, to: '/portfolio' },
          { label: project.data.key },
        ]}
      />

      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1">
        <h1 className="text-ink-900 text-xl font-semibold tracking-tight">{project.data.name}</h1>
        {project.data.client === null ? null : (
          <span className="text-ink-500 flex items-center gap-1 text-[13px]">
            <Icon name="people" className="size-3.5" />
            {project.data.client}
          </span>
        )}
        {project.data.stage === null ? null : (
          <span className="border-brand-300 bg-brand-50 text-brand-700 rounded-full border px-2.5 py-0.5 font-mono text-[11px]">
            {project.data.stage}
          </span>
        )}
        <div className="ms-auto flex items-center gap-3">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="border-line text-ink-700 hover:bg-surface-2 flex items-center gap-1.5 rounded border px-2.5 py-1 text-[12px]"
          >
            <Icon name="pencil" className="size-3.5" />
            {t('project.edit')}
          </button>
          <Link
            to={`/boards/${project.data.key}`}
            className="text-ink-500 hover:text-ink-900 text-[12px] underline"
          >
            {t('view.openInBoard')}
          </Link>
        </div>
      </div>

      <ViewTabs tabs={tabs} active={layout} onSelect={(id) => setLayout(id as Layout)} />

      {SAVEABLE.includes(layout as ViewLayout) ? (
        <ViewBar
          projectKey={project.data.key}
          projectId={project.data.id}
          layout={layout as ViewLayout}
          activeViewId={view?.id ?? null}
          onPick={(picked) => {
            setView(picked)
            // A view is a filter *and* a rendering (ADR-0003). Picking a board
            // view while looking at the list used to change the filter and
            // leave you on the list, which is half of what was asked for.
            if (picked !== null) setLayout(picked.layout)
          }}
        />
      ) : null}

      <div className="pt-2">
        {layout === 'list' ? (
          items.data === undefined ? (
            <p className="text-ink-500 font-mono text-xs">{t('common.loading')}</p>
          ) : (
            <TaskList
              items={items.data}
              asOf={items.dataUpdatedAt}
              onOpen={(item) => setOpen(item)}
              projectId={project.data.id}
            />
          )
        ) : null}

        {layout === 'table' ? (
          items.data === undefined ? (
            <p className="text-ink-500 font-mono text-xs">{t('common.loading')}</p>
          ) : (
            <TaskTable
              items={items.data}
              asOf={items.dataUpdatedAt}
              onOpen={(item) => setOpen(item)}
            />
          )
        ) : null}

        {layout === 'calendar' ? (
          items.data === undefined ? (
            <p className="text-ink-500 font-mono text-xs">{t('common.loading')}</p>
          ) : (
            <TaskCalendar items={items.data} onOpen={(item) => setOpen(item)} />
          )
        ) : null}

        {layout === 'board' ? <BoardPage projectKey={project.data.key} embedded /> : null}

        {layout === 'timeline' ? (
          timeline.data === undefined ? (
            <p className="text-ink-500 font-mono text-xs">{t('common.loading')}</p>
          ) : (
            <Timeline timeline={timeline.data} />
          )
        ) : null}

        {layout === 'documents' ? (
          <DocumentLibrary
            projectKey={project.data.key}
            onOpen={(document) => setOpenDocument(document)}
          />
        ) : null}

        {layout === 'activity' ? (
          <section className="border-line bg-surface rounded-md border p-4">
            <ActivityFeed project={project.data.key} />
          </section>
        ) : null}

        {layout === 'overview' ? (
          <div className="flex flex-col gap-6">
            <WorkflowPanel projectKey={project.data.key} />

            {project.data.solution === null ? null : (
              <section>
                <h2 className="text-ink-900 mb-2 text-sm font-semibold">{t('project.solution')}</h2>
                <p className="border-line bg-surface text-ink-700 rounded-md border p-4 text-sm">
                  {project.data.solution}
                </p>
              </section>
            )}

            {project.data.links.length === 0 ? null : (
              <section>
                <h2 className="text-ink-900 mb-2 text-sm font-semibold">{t('project.links')}</h2>
                <ul className="border-line bg-surface divide-line divide-y rounded-md border">
                  {project.data.links.map((link) => (
                    <li key={link} className="px-4 py-2.5">
                      <a
                        href={link}
                        target="_blank"
                        rel="noreferrer"
                        className="text-brand-600 text-sm break-all underline"
                      >
                        {link}
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        ) : null}
      </div>

      {open === null ? null : (
        <TaskPanel
          key={open.id}
          item={open}
          projectKey={project.data.key}
          onClose={() => setOpen(null)}
        />
      )}

      {openDocument === null ? null : (
        <DocumentPanel
          document={openDocument}
          projectKey={project.data.key}
          onClose={() => setOpenDocument(null)}
        />
      )}
      {editing ? (
        <ProjectSettings project={project.data} onClose={() => setEditing(false)} />
      ) : null}
    </div>
  )
}
